import { collection, getDocs, query, where, type FirestoreError } from 'firebase/firestore'

import type { Class, UserRole } from '@study-decks/shared'

import { getDb } from './firebase.js'

export class ClassApiError extends Error {
  override readonly name = 'ClassApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

type SnapshotLike = { id: string; data(): Record<string, unknown> }

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function stringList(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function classFromSnapshot(snapshot: SnapshotLike): Class {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    googleCourseId: stringValue(data.googleCourseId),
    name: stringValue(data.name),
    section: typeof data.section === 'string' ? data.section : null,
    teacherIds: stringList(data.teacherIds),
    studentIds: stringList(data.studentIds),
    pendingInviteEmails: stringList(data.pendingInviteEmails),
    domain: stringValue(data.domain),
    lastRosterSyncAt: stringValue(data.lastRosterSyncAt),
    createdAt: stringValue(data.createdAt),
    status: data.status === 'archived' ? 'archived' : 'active',
  }
}

function errorCode(err: unknown): string {
  const code = (err as Partial<FirestoreError>).code
  if (code === 'permission-denied') return 'PERMISSION_DENIED'
  if (code === 'unavailable') return 'UNAVAILABLE'
  return 'REQUEST_FAILED'
}

function errorMessage(code: string): string {
  if (code === 'PERMISSION_DENIED') return "You don't have access to these classes."
  if (code === 'UNAVAILABLE') return "Couldn't reach the database — check your connection."
  return 'Something went wrong — please try again.'
}

function normalizeError(err: unknown): ClassApiError {
  if (err instanceof ClassApiError) return err
  const code = errorCode(err)
  return new ClassApiError(code, errorMessage(code))
}

export async function listMyClasses(uid: string): Promise<Class[]> {
  try {
    const classesQuery = query(
      collection(getDb(), 'classes'),
      where('studentIds', 'array-contains', uid),
    )
    const snapshot = await getDocs(classesQuery)
    return snapshot.docs
      .map(classFromSnapshot)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function listMyTeacherClasses(uid: string, role: UserRole | null): Promise<Class[]> {
  try {
    const classesQuery =
      role === 'admin'
        ? query(collection(getDb(), 'classes'))
        : query(collection(getDb(), 'classes'), where('teacherIds', 'array-contains', uid))
    const snapshot = await getDocs(classesQuery)
    return snapshot.docs
      .map(classFromSnapshot)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  } catch (err) {
    throw normalizeError(err)
  }
}
